import React, { useState, useContext } from 'react';
import { useNavigate } from "react-router-dom";
import Azulao from '../Azulão.png';
import AppContext from '../api/AppContext';
import ChangePasswordModal from '../components/ChangePasswordModal';
import api from '../services/api';

const Login = () => {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotLogin, setForgotLogin] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  
  const navigate = useNavigate();
  const { login: loginFunction, logout  } = useContext(AppContext);

  // Função para limpar o CPF (remover pontos e traços)
  const cleanCPF = (value) => {
    // Remove tudo que não for número
    return value.replace(/[^\d]/g, '');
  };

  // Função para formatar CPF (opcional - para exibição)
  const formatCPF = (value) => {
    const numbers = cleanCPF(value);
    
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    } else if (numbers.length <= 9) {
      return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    } else {
      return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
    }
  };

  const handleLoginChange = (e) => {
    const value = e.target.value;
    
    // Se for CPF (contém apenas números ou já está sendo formatado)
    if (/^[\d.-]*$/.test(value)) {
      // Remove a formatação para armazenar apenas números
      const cleanedValue = cleanCPF(value);
      
      // Se tiver mais que 11 dígitos, limita
      if (cleanedValue.length <= 11) {
        // Aplica a formatação visual se desejar
        setLogin(formatCPF(cleanedValue));
      }
    } else {
      // É email - mantém como está
      setLogin(value);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    if (login === '' || password === '') {
      setMessage('Por favor, preencha todos os campos.');
      setIsLoading(false);
      return;
    }

    try {
      // Prepara o login para envio
      let loginToSend = login;
      
      // Se for CPF (contém apenas números, pontos e traços)
      if (/^[\d.-]*$/.test(login)) {
        // Remove todos os caracteres não numéricos antes de enviar
        loginToSend = cleanCPF(login);
      }
      
      const response = await loginFunction({ login: loginToSend, password });
      
      setMessage('Login bem-sucedido!');
      setUserData(response.data.user);
      
      if (response.data.user.firstLogin) {
        setShowChangePasswordModal(true);
      } else {
        if (response.data.user.enderecoVerification?.status === 'rejected') {
          const reason = response.data.user.enderecoVerification.rejectionReason || 'Documentação não validada';
          alert(`⚠️Atenção: Seu endereço foi rejeitado.\n\nMotivo: ${reason}\n\nPor favor, atualize seu endereço na seção Perfil para submeter novamente.`);
        }
        redirectUser(response.data.user.role);
      }

    } catch (error) {
      // Mensagem mais específica para erro de credenciais
      setMessage('Senha incorreta. Verifique seus dados.');
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    logout(); // desloga o usuário
    setShowChangePasswordModal(false);
    setMessage('Faça login novamente.');
    
  };

  const handlePasswordChangeSuccess = () => {
    setMessage('Senha alterada com sucesso! Redirecionando...');
    setShowChangePasswordModal(false);
    
    if (userData) {
      setMessage('Senha alterada com sucesso! Faça login novamente.');
      setTimeout(() => {
        logout();
      }, 1500);
    }
  };

  const redirectUser = (role) => {
    switch (role) {
      case 'admin':
        navigate('/dashboard');
        break;
      case 'member':
        navigate('/member/dashboard');
        break;
      case 'staff':
        navigate('/staff/dashboard');
        break;
      default:
        navigate('/');
    }
  };

  const handleForgotPassword = () => {
    setForgotLogin('');
    setForgotMessage('');
    setShowForgotModal(true);
  };

  const handleSubmitForgot = async (e) => {
    e.preventDefault();
    if (!forgotLogin.trim()) {
      setForgotMessage('Informe seu e-mail ou CPF cadastrado.');
      return;
    }
    setForgotLoading(true);
    setForgotMessage('');
    try {
      const response = await api.post('/auth/forgot-password', { login: forgotLogin });
      setForgotMessage(response.data.message || 'Link de recuperação enviado para o e-mail cadastrado.');
    } catch (error) {
      setForgotMessage(error.response?.data?.message || 'Erro ao solicitar recuperação. Tente novamente.');
    } finally {
      setForgotLoading(false);
    }
  };

  // Determina as propriedades do input baseado no conteúdo
  const getInputProps = () => {
    const hasAtSymbol = login.includes('@');
    const onlyNumbersAndSpecial = /^[\d.-]*$/.test(login);
    
    if (hasAtSymbol) {
      return {
        type: 'email',
        placeholder: 'exemplo@email.com',
        maxLength: undefined
      };
    } else {
      return {
        type: 'text',
        placeholder: 'Digite seu CPF (com ou sem pontuação)',
        maxLength: 14 // 11 números + 3 pontos + 1 traço = 15, mas vamos limitar a 14 para dar espaço
      };
    }
  };

  const inputProps = getInputProps();

  return (
    <div style={styles.container}>
        <div style={styles.img}>
            <img
            src={Azulao}
            alt="Banner"
            style={styles.image}
            />
        </div>
      <div style={styles.card}>
        <h2 style={styles.title}>Entre no App Olympic Club</h2>

        <form style={styles.form} onSubmit={handleLogin}>
          <label style={styles.label}>CPF ou E-mail:</label>
          <input
            type={inputProps.type}
            placeholder={inputProps.placeholder}
            value={login}
            onChange={handleLoginChange}
            style={styles.input}
            maxLength={inputProps.maxLength}
          />

          <label style={styles.label}>Senha:</label>
          <div style={styles.passwordWrapper}>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.inputPassword}
            />
            <span
              onClick={() => setShowPassword(!showPassword)}
              style={styles.eyeIcon}
            >
              {showPassword ? '🙈' : '👁️'}
            </span>
          </div>

          <button 
            type="submit" 
            style={styles.button}
            disabled={isLoading}
          >
            {isLoading ? 'CARREGANDO...' : 'ENTRAR'}
          </button>
          <button
            type="button"
            onClick={handleForgotPassword}
            style={styles.forgot}
          >
            Esqueci minha senha
          </button>

          {message && <p style={styles.message}>{message}</p>}
        </form>

        <ChangePasswordModal
          isOpen={showChangePasswordModal}
          onClose={() => handleCloseModal()}
          onSuccess={handlePasswordChangeSuccess}
          userData={userData}
        />
      </div>

      {showForgotModal && (
        <div style={styles.modalOverlay} onClick={() => setShowForgotModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Recuperar Senha</h3>
            <p style={styles.modalText}>
              Informe seu e-mail ou CPF cadastrado. Enviaremos um link de redefinição de senha para o e-mail.
            </p>
            <form onSubmit={handleSubmitForgot}>
              <input
                type="text"
                value={forgotLogin}
                onChange={(e) => setForgotLogin(e.target.value)}
                placeholder="E-mail ou CPF"
                style={styles.forgotInput}
              />
              {forgotMessage && <p style={styles.forgotMessage}>{forgotMessage}</p>}
              <div style={styles.forgotActions}>
                <button type="button" onClick={() => setShowForgotModal(false)} style={styles.forgotCancel}>
                  Cancelar
                </button>
                <button type="submit" disabled={forgotLoading} style={styles.forgotSubmit}>
                  {forgotLoading ? 'Enviando...' : 'Enviar link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: '#f3f5ff',
    minHeight: '100vh',
    minHeight: '100dvh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: '22px',
    boxSizing: 'border-box',
  },
  img: {},
  card: {
    width: '340px',
    backgroundColor: '#f3f5ff',
    borderRadius: '20px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    padding: '20px',
    textAlign: 'center',
  },
  image: {
    width: '100%',
    height: '180px',
    objectFit: 'cover',
  },
  title: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#1f3b7d',
    margin: '20px 0',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '10px',
    paddingTop: '10px',
  },
  label: {
    textAlign: 'left',
    fontSize: '14px',
    color: '#444',
    marginBottom: '-8px',
  },
  input: {
    height: '35px',
    borderRadius: '20px',
    border: '1px solid #ccc',
    padding: '0 12px',
    fontSize: '14px',
    outline: 'none',
  },
  passwordWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  inputPassword: {
    flex: 1,
    height: '35px',
    borderRadius: '20px',
    border: '1px solid #007bff',
    padding: '0 40px 0 12px',
    fontSize: '14px',
    outline: 'none',
  },
  eyeIcon: {
    position: 'absolute',
    right: '12px',
    cursor: 'pointer',
    fontSize: '16px',
  },
  button: {
    marginTop: '10px',
    backgroundColor: '#2a89ff',
    color: '#fff',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '10px',
    height: '40px',
    cursor: 'pointer',
    fontSize: '14px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
    marginTop: '20px',
  },
  forgot: {
    backgroundColor: 'transparent',
    border: 'none',
    color: 'red',
    fontSize: '14px',
    marginTop: '10px',
    cursor: 'pointer',
    textDecoration: 'none',
  },
  message: {
    marginTop: '10px',
    fontSize: '14px',
    color: 'red',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '24px',
    width: '90%',
    maxWidth: '380px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
  },
  modalTitle: {
    margin: '0 0 12px 0',
    fontSize: '18px',
    color: '#1f3b7d',
    textAlign: 'center',
  },
  modalText: {
    fontSize: '14px',
    color: '#555',
    marginBottom: '16px',
    textAlign: 'center',
  },
  forgotInput: {
    width: '100%',
    height: '38px',
    borderRadius: '8px',
    border: '1px solid #ccc',
    padding: '0 12px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  forgotMessage: {
    marginTop: '10px',
    fontSize: '13px',
    color: '#1A3B7D',
    textAlign: 'center',
  },
  forgotActions: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '10px',
    marginTop: '16px',
  },
  forgotCancel: {
    flex: 1,
    padding: '10px',
    borderRadius: '8px',
    border: '1px solid #ccc',
    backgroundColor: '#f5f5f5',
    cursor: 'pointer',
    fontSize: '14px',
  },
  forgotSubmit: {
    flex: 1,
    padding: '10px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#2a89ff',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
  },
};

export default Login;